import { ref, onUnmounted, watch } from 'vue';
import { hasNavigation, hasPagination, hasScrollbar } from './helpers/swiperHelpers';

/**
 * Composable to detect user interactions with Swiper.
 * It sets a flag `isUserInteraction` to true when the user interacts with the Swiper instance,
 * and resets it when the Swiper's real index changes.
 * @param {import('vue').Ref<Object | null>} swiperInstanceRef - A ref to the Swiper instance.
 * @param {function(string): void} onScrollTrackedCallback - Callback function to be called when a scroll interaction is detected.
 *   It receives the scroll direction as an argument.
 * @returns {void}
 */
export function useSwiperInteractionDetector(swiperInstanceRef, onScrollTrackedCallback) {
  const initialized = ref(false);
  const isUserInteraction = ref(false);
  let swiper = null;
  const realPrevIndex = ref(-1);

  const handleUserInteraction = () => {
    isUserInteraction.value = true;
    if (swiper.realIndex === realPrevIndex.value) {
      return;
    }

    const currentRealIndex = swiper.realIndex;
    const previousRealIndex = realPrevIndex.value;
    const direction = currentRealIndex > previousRealIndex ? 'next' : 'prev';

    if (onScrollTrackedCallback) {
      onScrollTrackedCallback(direction);
    }

    updateRealPrevIndex();
  };

  const onRealSlideChange = () => {
    if (isUserInteraction.value) {
      handleUserInteraction();
      isUserInteraction.value = false;
    }
  };

  const updateRealPrevIndex = () => {
    if (swiper) {
      realPrevIndex.value = swiper.realIndex;
    }
  };

  watch(
    swiperInstanceRef,
    (newSwiperInstance) => {
      if (newSwiperInstance && !initialized.value) {
        swiper = newSwiperInstance;
        updateRealPrevIndex();
        swiper.on('touchStart', handleUserInteraction);
        swiper.on('realIndexChange', onRealSlideChange);
        swiper.on('click', handleUserInteraction);
        if (hasNavigation(swiper)) {
          swiper.navigation.nextEl.addEventListener?.('click', handleUserInteraction);
          swiper.navigation.prevEl.addEventListener?.('click', handleUserInteraction);
        }
        if (hasPagination(swiper)) {
          swiper.pagination.bullets.forEach((bullet) => {
            bullet.addEventListener('click', handleUserInteraction);
          });
        }
        if (hasScrollbar(swiper)) {
          swiper.scrollbar.el.addEventListener('click', handleUserInteraction);
        }
        initialized.value = true;
      }
    },
    { immediate: true }
  );

  onUnmounted(() => {
    if (swiper && initialized.value) {
      swiper.off('touchStart', handleUserInteraction);
      swiper.off('realIndexChange', onRealSlideChange);
      swiper.off('click', handleUserInteraction);
      if (hasNavigation(swiper)) {
        swiper.navigation.nextEl.removeEventListener?.('click', handleUserInteraction);
        swiper.navigation.prevEl.removeEventListener?.('click', handleUserInteraction);
      }
      if (hasPagination(swiper)) {
        swiper.pagination.bullets.forEach((bullet) => {
          bullet.removeEventListener('click', handleUserInteraction);
        });
      }
      if (hasScrollbar(swiper)) {
        swiper.scrollbar.el.removeEventListener('click', handleUserInteraction);
      }
    }
  });
}
